import * as React from "react";
import { type TypeButtonProps } from "@sproutsocial/seeds-react-button";
import { Icon } from "@sproutsocial/seeds-react-icon";
import { ActionMenu } from "./ActionMenu";
import {
  MenuButtonContainer,
  MainButton,
  TriggerButton,
} from "./MenuButtonStyles";

// ─── MenuButton ─────────────────────────────────────────────────────────────

export interface MenuButtonProps extends TypeButtonProps {
  /**
   * Menu item content rendered inside the dropdown half — the children of an
   * ActionMenu (MenuItems / MenuGroups).
   */
  actionMenu: React.ReactNode;
  /**
   * Accessible label for the chevron-only trigger button. Required so consumers
   * supply a localizable label rather than relying on a hardcoded fallback.
   */
  menuButtonAriaLabel: string;
}

/**
 * A "split button": a primary Button rendered immediately next to an ActionMenu
 * whose trigger is an identical Button showing only a chevron-down icon. The
 * two read as one control — the main button's right corners and the trigger's
 * left corners are squared off with a 1px gap between them.
 */
export function MenuButton({
  actionMenu,
  appearance = "primary",
  size,
  disabled,
  menuButtonAriaLabel,
  children,
  ...rest
}: MenuButtonProps) {
  return (
    <MenuButtonContainer>
      <MainButton
        appearance={appearance}
        size={size}
        disabled={disabled}
        {...rest}
      >
        {children}
      </MainButton>
      <ActionMenu
        disabled={disabled}
        trigger={
          <TriggerButton
            appearance={appearance}
            size={size}
            disabled={disabled}
            ariaLabel={menuButtonAriaLabel}
          >
            <Icon name="chevron-down-outline" fixedWidth aria-hidden />
          </TriggerButton>
        }
      >
        {actionMenu}
      </ActionMenu>
    </MenuButtonContainer>
  );
}
